import type { ReactNode } from "react";

/**
 * Shared setting-row label component used by both the dashboard's
 * embedded audit creator and the standalone /audit/new page. Each
 * row gets a leading glyph + humanized sentence-case phrase, which
 * gave the eye a unique fingerprint per row instead of a wall of
 * uppercase columns. Extracted here so both surfaces stay in sync —
 * before, every label change had to be made twice.
 */
export function SettingLabel({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className="rc-label">
      <span className="rc-label-icon" aria-hidden="true">{icon}</span>
      <span className="rc-label-text">{children}</span>
    </span>
  );
}

/* ------------------ Row glyphs ------------------ */

export function OutputGlyph() {
  // File with checkmark — "the deliverable / what comes out".
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <polyline points="9 15 11 17 15 13" />
    </svg>
  );
}

export function ScopeGlyph() {
  // Target / bullseye — "how wide a net to cast".
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function DeviceGlyph() {
  // Monitor with stand — the in-button toggles carry their own
  // desktop/mobile glyphs; this row glyph just signals "device".
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

export function PagesGlyph() {
  // Two overlapping sheets — "multiple pages / copies".
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/* ------------------ Plan-gate helper ------------------ */

/**
 * Cooldown window (ms) between successive opens. Without this, a
 * user who clicks three locked Pro chips in a row would get three
 * pricing tabs — tab-spam from a single intent. 1.5s is short
 * enough that a deliberate second open (revisiting after some
 * thought) still works, but long enough to absorb the "click each
 * locked chip to see what it does" scan pattern.
 */
const UPGRADE_OPEN_COOLDOWN_MS = 1500;
let lastUpgradeOpenAt = 0;

/**
 * Open the in-app upgrade ladder (Account → Plan & billing, #upgrade)
 * in a new tab. Used by locked plan-gated chips/accordions so a click
 * does something useful instead of being silently ignored. New tab
 * keeps the in-progress audit config alive; the destination stays
 * INSIDE the tool — upgrades shouldn't bounce signed-in users out to
 * the marketing site (matches the sidebar "Upgrade plan" link).
 *
 * Rate-limited so rapid clicks across multiple locked controls only
 * spawn one tab.
 */
export function openUpgradeInNewTab() {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastUpgradeOpenAt < UPGRADE_OPEN_COOLDOWN_MS) return;
  lastUpgradeOpenAt = now;
  window.open("/account#upgrade", "_blank", "noopener,noreferrer");
}

/* ------------------ URL chip label helper ------------------ */

/**
 * Recent-URL chip label. Two audits of acme.com/landing and
 * acme.com/pricing should be distinguishable — hostname alone
 * collapses them visually. Appends the path when it's not "/",
 * truncated so a long path doesn't blow out the chip width.
 */
export function chipLabelFor(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname.replace(/\/+$/, "");
    if (!path || path === "") return host;
    const compact = path.length > 22 ? `${path.slice(0, 21)}…` : path;
    return `${host}${compact}`;
  } catch {
    return url;
  }
}

/* ------------------ Audit defaults persistence ------------------ */

/**
 * Shared localStorage key for the Settings page's saved audit
 * preferences. Both surfaces that create audits (/audit/new and the
 * dashboard's embedded ReviewCreator) read this so a user's saved
 * defaults actually pre-fill the form — before this, Settings was a
 * write-only sink that didn't influence behavior anywhere in the
 * product.
 */
export const AUDIT_DEFAULTS_STORAGE_KEY = "snap-site:settings";

export type AuditDefaults = {
  devicePreference: "desktop" | "mobile" | "both";
  defaultPages: number;
  aiGuidance: boolean;
};

/**
 * Read persisted audit defaults from localStorage. Returns null when
 * there's nothing saved (first-time user) or the blob fails to parse
 * — callers fall back to their own hardcoded defaults in that case.
 * Safe to call during SSR (returns null).
 */
export function loadAuditDefaults(): AuditDefaults | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AUDIT_DEFAULTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuditDefaults> & {
      scopePreference?: "full" | "specific";
    };
    if (
      parsed.devicePreference === undefined &&
      parsed.scopePreference === undefined &&
      parsed.defaultPages === undefined &&
      parsed.aiGuidance === undefined
    ) {
      return null;
    }
    // Migration: the old "Single page" scope was never more than a
    // 1-page run, so collapse a saved scopePreference into the page
    // count. The single page-count control is now the source of truth.
    const defaultPages =
      parsed.scopePreference === "specific" ? 1 : parsed.defaultPages ?? 3;
    return {
      devicePreference: parsed.devicePreference ?? "desktop",
      defaultPages,
      aiGuidance: parsed.aiGuidance ?? false,
    };
  } catch {
    return null;
  }
}

/**
 * Read the user's chosen display name from the Settings → Profile snapshot
 * (same localStorage blob as the audit defaults). Returns null when unset, so
 * callers fall back to the email-derived name. Safe to call during SSR.
 */
export function loadDisplayName(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AUDIT_DEFAULTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { name?: unknown };
    const name = typeof parsed.name === "string" ? parsed.name.trim() : "";
    return name || null;
  } catch {
    return null;
  }
}
